import type { PartyId } from '../types';
import {
  LATEST_YEAR,
  PREVIOUS_YEAR,
  type ElectionSectionData,
  type ElectionYear,
} from './index';

/**
 * Riksdag results per valdistrikt, generated by
 * `scripts/fetch-district-elections.mjs` into `valdistrikt.json`. Kept apart
 * from `./index` because the file is large: the app requires it lazily and the
 * web fetches it from /elections/valdistrikt.json, so nothing here imports it.
 */

export interface DistrictElectionYear {
  /** Latest year only: districts val.se marks as comparable with the previous election. */
  comparableWithPrevious?: string[];
  /** District id → shares in `meta.parties` order. */
  districts: Record<string, number[]>;
  /** 4-digit municipality code → shares, the fallback for redrawn districts. */
  kommuner: Record<string, number[]>;
  kommunNames: Record<string, string>;
}

export interface DistrictElectionData {
  meta: { source: string; url: string; fetchedAt: string; parties: PartyId[] };
  years: Partial<Record<ElectionYear, DistrictElectionYear>>;
}

export interface DistrictResult {
  results: Partial<Record<PartyId, number>>;
  /** 'kommun' when the district did not exist in that election. */
  level: 'district' | 'kommun';
  kommunName?: string;
}

function toRecord(parties: PartyId[], shares: number[]): Partial<Record<PartyId, number>> {
  const out: Partial<Record<PartyId, number>> = {};
  parties.forEach((p, i) => { out[p] = shares[i]; });
  return out;
}

/** The district's result in `year`, falling back to its kommun if it was redrawn. */
export function districtResult(
  data: DistrictElectionData,
  districtId: string,
  year: ElectionYear,
): DistrictResult | null {
  const y = data.years[year];
  if (!y) return null;
  const own = y.districts[districtId];
  if (own) return { results: toRecord(data.meta.parties, own), level: 'district' };
  const kod = districtId.slice(0, 4);
  const kommun = y.kommuner[kod];
  if (!kommun) return null;
  return { results: toRecord(data.meta.parties, kommun), level: 'kommun', kommunName: y.kommunNames[kod] };
}

/**
 * Whether the latest result for this district can be compared with the
 * previous one: both are district-level and val.se says the boundaries match.
 */
export function districtComparable(data: DistrictElectionData, districtId: string): boolean {
  const latest = data.years[LATEST_YEAR];
  const previous = data.years[PREVIOUS_YEAR];
  return !!latest?.districts[districtId]
    && !!previous?.districts[districtId]
    && !!latest.comparableWithPrevious?.includes(districtId);
}

function sectionTitle(year: ElectionYear, r: DistrictResult, redrawn: boolean): string {
  if (r.level === 'kommun') return `Riksdagsvalet ${year} — ${r.kommunName ?? 'hela kommunen'}`;
  return redrawn ? `Riksdagsvalet ${year} — ny distriktsindelning` : `Riksdagsvalet ${year}`;
}

/** Popup sections for a district, latest year first. */
export function districtElectionSections(
  data: DistrictElectionData,
  districtId: string,
): ElectionSectionData[] {
  const comparable = districtComparable(data, districtId);
  return [LATEST_YEAR, PREVIOUS_YEAR].flatMap((year) => {
    const r = districtResult(data, districtId, year);
    if (!r) return [];
    // The map is drawn from 2022 districts, so only a later year can be redrawn.
    const redrawn = year === LATEST_YEAR && r.level === 'district' && !comparable
      && !!data.years[PREVIOUS_YEAR]?.districts[districtId];
    return [{ title: sectionTitle(year, r, redrawn), results: r.results }];
  });
}
